import { useTranslation } from "react-i18next";
import type { ReportInsight } from "@normax-performance/shared-types";

export interface ReportInsightsListProps {
  insights: ReportInsight[];
}

/** Every insight is a deterministic `{key, params}` rendered through the
 * `reports` namespace's `insights.*` templates (evidence-only phrasing,
 * enforced at the i18n-parity test level — task spec §20). No prose is ever
 * assembled in this component; it only interpolates.
 *
 * premium-presentation-polish: each insight renders as one quiet row inside
 * a single contained panel — a small neutral marker (never a zone/sentiment
 * color, since a `ReportInsight` carries no zone data to color it honestly
 * by) plus the evidence text, separated by hairline dividers. Deliberately
 * NOT a speech-bubble/AI-card look — these are deterministic system facts,
 * not suggestions. */
export function ReportInsightsList({ insights }: ReportInsightsListProps) {
  const { t } = useTranslation("reports");
  if (insights.length === 0) return null;
  return (
    <section className="nx-report-insights" aria-labelledby="nx-report-insights-heading">
      <h2 id="nx-report-insights-heading" className="nx-report-panel__title">
        {t("sections.insights")}
      </h2>
      <ul className="nx-report-insights__list">
        {insights.map((insight, index) => (
          <li key={`${insight.key}-${index}`} className="nx-report-insights__item">
            <span className="nx-report-insights__marker" aria-hidden="true" />
            <span className="nx-report-insights__text">{t(`insights.${insight.key}`, insight.params)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
